import { datadogRum } from '@datadog/browser-rum';
import {
  type AnonymousLaunchDarklyContext,
  formatLaunchDarklyUserContext,
  type UserLaunchDarklyContext,
} from '@side/launch-darkly';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { type ReactNode, useContext } from 'react';
import { UserInfoContext } from 'src/components/UserInfoProvider';
import { subscribedFlags } from './flags';
import { getOrCreateClientId } from './utils';

type LaunchDarklyProps = {
  children: ReactNode;
};

/**
 * Initialize the Launch Darkly React SDK
 */
export function LaunchDarkly({ children }: LaunchDarklyProps) {
  const { userinfo, isLoading } = useContext(UserInfoContext);

  // We have to render children until userinfo is loaded since only first context is sent to LD
  // NOTE: Rendering children can have consequences of features using flags "flickering" until LD is initialized
  // we consider this acceptable instead of returning null so that other rendering/fetching can start
  if (isLoading) {
    return children;
  }

  let context: UserLaunchDarklyContext | AnonymousLaunchDarklyContext = {
    key: 'unknown',
    kind: 'user',
  };

  if (userinfo) {
    context = formatLaunchDarklyUserContext(userinfo);
  }

  // https://docs.cypress.io/faq/questions/using-cypress-faq#Is-there-any-way-to-detect-if-my-app-is-running-under-Cypress
  if (typeof window !== 'undefined' && window.Cypress) {
    context = {
      key: 'cypress',
      anonymous: false,
      kind: 'user',
    };
  }

  context = {
    ...context,
    custom: { clientId: getOrCreateClientId() },
  };

  return (
    <LDProvider
      clientSideID={process.env.NEXT_PUBLIC_LAUNCH_DARKLY_SDK_ID ?? ''}
      context={context}
      flags={subscribedFlags}
      timeout={5}
      options={{
        bootstrap: 'localStorage',
        inspectors: [
          {
            type: 'flag-used',
            name: 'dd-inspector',
            method: (key, detail) => {
              datadogRum.addFeatureFlagEvaluation(key, detail.value);
            },
          },
        ],
      }}
    >
      {children}
    </LDProvider>
  );
}
